/**
 * WA0268 · Página pública de firma de documentos PDF (multi-firmante).
 * URL: /firmar-doc/:token (sin auth)
 */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import axios from "axios";
import { CheckCircle2, RotateCcw, Loader2, ShieldCheck, FileText, XCircle, Users, Fingerprint } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ESTADO_LABEL = { enviado: "⏳ Pendiente", abierto: "👁 Abierto", firmado: "✅ Firmado", rechazado: "❌ Rechazado", caducado: "⌛ Caducado", en_espera: "🕐 En espera de turno" };

export default function FirmaDocPublicaPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [nombre, setNombre] = useState("");
  const [dni, setDni] = useState("");
  const [acepta, setAcepta] = useState(false);
  const [resultado, setResultado] = useState(null); // "firmado" | "rechazado"
  const [modoRechazo, setModoRechazo] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [pdfUrl, setPdfUrl] = useState(null);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const empty = useRef(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND}/api/firmar-doc/${token}`);
        setData(r.data);
        setNombre(r.data.firmante?.nombre || "");
        if (r.data.firmante?.estado === "firmado") setResultado("firmado");
        if (r.data.firmante?.estado === "rechazado") setResultado("rechazado");
        const pdf = await fetch(`${BACKEND}/api/firmar-doc/${token}/pdf`);
        if (pdf.ok) setPdfUrl(URL.createObjectURL(await pdf.blob()));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Enlace inválido o caducado");
      } finally { setLoading(false); }
    })();
  }, [token]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.strokeStyle = "#0F1A3C"; ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.lineJoin = "round";
    const pos = (e) => {
      const r = c.getBoundingClientRect();
      const t = e.touches?.[0];
      return { x: (t ? t.clientX : e.clientX) - r.left, y: (t ? t.clientY : e.clientY) - r.top };
    };
    const start = (e) => { drawing.current = true; empty.current = false; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
    const move = (e) => { if (!drawing.current) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); e.preventDefault(); };
    const end = () => { drawing.current = false; };
    c.addEventListener("mousedown", start); c.addEventListener("mousemove", move);
    c.addEventListener("mouseup", end); c.addEventListener("mouseleave", end);
    c.addEventListener("touchstart", start); c.addEventListener("touchmove", move);
    c.addEventListener("touchend", end);
    return () => {
      c.removeEventListener("mousedown", start); c.removeEventListener("mousemove", move);
      c.removeEventListener("mouseup", end); c.removeEventListener("mouseleave", end);
      c.removeEventListener("touchstart", start); c.removeEventListener("touchmove", move);
      c.removeEventListener("touchend", end);
    };
  }, [resultado, data, modoRechazo, loading]);

  const limpiar = () => {
    const c = canvasRef.current; if (!c) return;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    empty.current = true;
  };

  const firmar = async () => {
    if (!nombre.trim()) return toast.error("Escribe tu nombre completo");
    if (data?.firmante?.dni_requerido && !dni.trim()) return toast.error("El DNI/NIF es obligatorio");
    if (!acepta) return toast.error("Debes aceptar el contenido para firmar");
    if (empty.current) return toast.error("Dibuja tu firma antes de confirmar");
    try {
      setEnviando(true);
      await axios.post(`${BACKEND}/api/firmar-doc/${token}/firmar`, {
        firma_dataurl: canvasRef.current.toDataURL("image/png"), nombre, dni: dni || null,
      });
      setResultado("firmado");
      toast.success("Firma registrada correctamente");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar la firma");
    } finally { setEnviando(false); }
  };

  const rechazar = async () => {
    if (motivo.trim().length < 3) return toast.error("Indica el motivo del rechazo");
    try {
      setEnviando(true);
      await axios.post(`${BACKEND}/api/firmar-doc/${token}/rechazar`, { motivo });
      setResultado("rechazado");
      toast.success("Rechazo registrado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar el rechazo");
    } finally { setEnviando(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
      <Loader2 className="h-6 w-6 animate-spin text-[#1E5BCC]" />
    </div>
  );
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <div className="max-w-md text-center p-8 bg-white rounded-xl shadow-lg">
        <FileText className="h-12 w-12 mx-auto text-red-500 mb-3" />
        <h1 className="text-lg font-extrabold">Enlace no válido</h1>
        <p className="text-sm text-slate-600 mt-2">Solicita al remitente un nuevo enlace de firma.</p>
      </div>
    </div>
  );

  const bloqueado = ["cancelado", "caducado", "rechazado"].includes(data.estado_documento) && !resultado;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 py-8 px-4">
      <Toaster position="top-center" richColors />
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="firma-doc-page">
        <div className="p-6 text-white" style={{ background: "linear-gradient(135deg,#0F1A3C,#1E5BCC)" }}>
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-8 w-8" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-extrabold opacity-70">LEGATUM CAPITAL · Firma electrónica de documentos</div>
              <h1 className="text-xl font-extrabold truncate" data-testid="firma-doc-titulo">{data.titulo}</h1>
              <div className="text-xs opacity-80 mt-1">
                Firmante: {data.firmante?.nombre}{data.obra_ref ? ` · Obra: ${data.obra_ref}` : ""} · Solicitado por {data.solicitado_por}
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 text-[10px] opacity-80">
            <Fingerprint className="h-3.5 w-3.5" />
            <span className="font-mono">SHA-256: {data.hash_sha256?.slice(0, 32)}…</span>
          </div>
        </div>

        {data.mensaje && (
          <div className="px-6 py-3 bg-amber-50 border-b border-amber-200 text-sm text-amber-900" data-testid="firma-doc-mensaje">
            <b>Mensaje del emisor:</b> {data.mensaje}
          </div>
        )}

        {data.otros_firmantes?.length > 0 && (
          <div className="px-6 py-2 border-b bg-slate-50 flex items-center gap-3 flex-wrap text-[11px]" data-testid="firma-doc-otros">
            <span className="font-extrabold text-slate-500 inline-flex items-center gap-1"><Users className="h-3 w-3" /> Otros firmantes:</span>
            {data.otros_firmantes.map((f, i) => (
              <span key={i} className="px-2 py-0.5 rounded-full bg-white border border-slate-200">
                {f.nombre} · {ESTADO_LABEL[f.estado] || f.estado}
              </span>
            ))}
          </div>
        )}

        <div className="p-6 border-b bg-white">
          <div className="text-[10px] uppercase tracking-widest font-extrabold text-slate-500 mb-2">Documento a firmar (revísalo completo)</div>
          {pdfUrl ? (
            <iframe src={pdfUrl} title="Documento PDF" data-testid="firma-doc-pdf"
                    className="w-full h-[55vh] rounded-lg border border-slate-200 bg-slate-50" />
          ) : (
            <div className="h-40 flex items-center justify-center text-sm text-slate-500 rounded-lg border border-slate-200 bg-slate-50">
              Cargando documento…
            </div>
          )}
        </div>

        {resultado === "firmado" ? (
          <div className="p-8 text-center" data-testid="firma-doc-confirmada">
            <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-500 mb-3" />
            <h2 className="text-lg font-extrabold text-emerald-700">Firma registrada</h2>
            <p className="text-sm text-slate-600 mt-2">
              Gracias, {nombre}. Cuando todos los firmantes completen la firma, el emisor recibirá el documento final sellado.
            </p>
          </div>
        ) : resultado === "rechazado" ? (
          <div className="p-8 text-center" data-testid="firma-doc-rechazada">
            <XCircle className="h-14 w-14 mx-auto text-red-500 mb-3" />
            <h2 className="text-lg font-extrabold text-red-700">Documento rechazado</h2>
            <p className="text-sm text-slate-600 mt-2">Se ha notificado al emisor con tu motivo.</p>
          </div>
        ) : bloqueado ? (
          <div className="p-8 text-center" data-testid="firma-doc-bloqueada">
            <XCircle className="h-14 w-14 mx-auto text-slate-400 mb-3" />
            <h2 className="text-lg font-extrabold text-slate-700">Esta solicitud está {data.estado_documento}</h2>
            <p className="text-sm text-slate-600 mt-2">Contacta con el emisor si necesitas un nuevo enlace.</p>
          </div>
        ) : data.firmante?.estado === "en_espera" ? (
          <div className="p-8 text-center" data-testid="firma-doc-en-espera">
            <Users className="h-14 w-14 mx-auto text-amber-500 mb-3" />
            <h2 className="text-lg font-extrabold text-amber-700">Aún no es tu turno de firma</h2>
            <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
              Este documento se firma en orden secuencial. Cuando firme el firmante anterior,
              recibirás un aviso por email y este enlace se activará automáticamente.
            </p>
          </div>
        ) : modoRechazo ? (
          <div className="p-6 space-y-4" data-testid="firma-doc-rechazo-form">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-extrabold text-slate-500">Motivo del rechazo *</label>
              <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3}
                        data-testid="firma-doc-motivo"
                        className="w-full mt-1 px-3 py-2 rounded border border-slate-300 text-sm"
                        placeholder="Explica por qué rechazas la firma de este documento…" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setModoRechazo(false)} className="flex-1 py-3 rounded-lg font-bold border border-slate-300 text-slate-600"
                      data-testid="firma-doc-rechazo-volver">← Volver</button>
              <button onClick={rechazar} disabled={enviando} data-testid="firma-doc-rechazo-confirmar"
                      className="flex-1 py-3 rounded-lg font-extrabold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50">
                {enviando ? <Loader2 className="h-4 w-4 inline animate-spin" /> : "Confirmar rechazo"}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-extrabold text-slate-500">Nombre y apellidos *</label>
                <input value={nombre} onChange={(e) => setNombre(e.target.value)} data-testid="firma-doc-nombre"
                       className="w-full mt-1 px-3 py-2 rounded border border-slate-300 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-extrabold text-slate-500">
                  DNI/NIF {data.firmante?.dni_requerido ? "*" : "(opcional)"}
                </label>
                <input value={dni} onChange={(e) => setDni(e.target.value)} data-testid="firma-doc-dni"
                       className="w-full mt-1 px-3 py-2 rounded border border-slate-300 text-sm" />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-extrabold text-slate-500 flex items-center justify-between">
                <span>Firma manuscrita *</span>
                <button onClick={limpiar} className="text-[10px] font-bold text-slate-500 inline-flex items-center gap-1 hover:text-slate-700"
                        data-testid="firma-doc-limpiar">
                  <RotateCcw className="h-3 w-3" /> Limpiar
                </button>
              </label>
              <canvas ref={canvasRef} width={700} height={220} data-testid="firma-doc-canvas"
                      className="w-full mt-1 border-2 border-dashed border-slate-300 rounded-lg bg-white cursor-crosshair touch-none" />
              <p className="text-[10px] text-slate-500 mt-1 italic">Dibuja tu firma con el ratón o con el dedo (dispositivos táctiles).</p>
            </div>

            <label className="flex items-start gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={acepta} onChange={(e) => setAcepta(e.target.checked)}
                     data-testid="firma-doc-acepta" className="mt-0.5" />
              <span>
                He leído el documento anterior y lo acepto en su integridad. Declaro que la firma dibujada es la mía
                y que actúo en nombre propio o con poder de representación suficiente. Acepto que se registren
                mi IP, fecha-hora y la huella SHA-256 del documento como evidencias de la firma.
              </span>
            </label>

            <div className="flex gap-3">
              <button onClick={() => setModoRechazo(true)} data-testid="firma-doc-rechazar"
                      className="py-3 px-5 rounded-lg font-bold border border-red-300 text-red-600 hover:bg-red-50">
                <XCircle className="h-4 w-4 mr-1 inline" /> Rechazar
              </button>
              <button onClick={firmar} disabled={enviando} data-testid="firma-doc-firmar"
                      className="flex-1 py-3 rounded-lg font-extrabold text-white shadow-md hover:shadow-lg transition-shadow disabled:opacity-50"
                      style={{ background: "linear-gradient(135deg,#0F8A5F,#0A6644)" }}>
                {enviando ? <><Loader2 className="h-4 w-4 mr-2 inline animate-spin" /> Registrando…</>
                          : <><CheckCircle2 className="h-4 w-4 mr-2 inline" /> Confirmar y firmar</>}
              </button>
            </div>
          </div>
        )}

        <div className="text-center py-3 text-[10px] text-slate-500 border-t border-slate-100">
          Firma manuscrita electrónica con registro de IP, sello temporal y hash SHA-256. Powered by LEGATUM CAPITAL.
        </div>
      </div>
    </div>
  );
}
